/**
 * Pure helpers for Cohesive public-file URLs embedded in HTML bodies —
 * extraction and presign reversal. No API calls; the presign round trip
 * itself lives in user-dashboard (useHtmlPresignRewrite), which owns the
 * authenticated client.
 */

/**
 * Matches `https://file.cohesive.ai/public/{id}` and any environment
 * subdomain variant (`file.local.cohesive.ai`, `file.dev.cohesive.ai`, …).
 * The optional middle segment is restricted to a single label of
 * lowercase alphanumerics + hyphens to avoid accidentally matching
 * unrelated hosts that happen to contain `cohesive.ai`.
 */
export const PUBLIC_URL_REGEX =
  /https:\/\/file\.(?:[a-z0-9-]+\.)?cohesive\.ai\/public\/([A-Za-z0-9_-]+)/g;

/** A Cohesive public-file reference scraped from an HTML body. */
export interface ReferencedFile {
  /** FileRecord id from the `/public/{id}` URL. */
  id: string;
  /** The full original public URL exactly as it appears in the HTML. */
  url: string;
}

/**
 * Extract the distinct Cohesive public-file references in `html`,
 * keeping the full URL alongside each id. Callers that can't fetch the
 * FileRecord itself (e.g. it belongs to another account — onboarding
 * decks embed shared template assets the viewing user doesn't own) fall
 * back to linking the raw public URL.
 */
export function extractReferencedFiles(html: string): ReferencedFile[] {
  const byId = new Map<string, string>();
  for (const match of html.matchAll(PUBLIC_URL_REGEX)) {
    const id = match[1];
    if (id && !byId.has(id)) byId.set(id, match[0]);
  }
  return Array.from(byId, ([id, url]) => ({ id, url }));
}

/** Replace every occurrence of `needle` (no regex escaping pitfalls —
 *  presigned URLs are full of regex metacharacters). */
function replaceAllLiteral(
  haystack: string,
  needle: string,
  replacement: string,
): string {
  if (!needle || !haystack.includes(needle)) return haystack;
  return haystack.split(needle).join(replacement);
}

/**
 * Reverse a presign rewrite: swap each presigned URL back to the public URL
 * it replaced, so island edits read off a live iframe DOM never persist
 * expiring URLs. Also handles the `&`→`&amp;` form — innerHTML serializes
 * ampersands in attribute values, and presigned query strings contain them.
 */
export function reversePresignedUrlsIn(
  html: string,
  presignedToPublic: ReadonlyMap<string, string>,
): string {
  let result = html;
  for (const [presigned, publicUrl] of presignedToPublic) {
    result = replaceAllLiteral(result, presigned, publicUrl);
    const escaped = presigned.split("&").join("&amp;");
    if (escaped !== presigned) {
      result = replaceAllLiteral(result, escaped, publicUrl);
    }
  }
  return result;
}
